import type { TFunction } from 'i18next';

import { visibleStage } from '../constants/hidden-stages';

import type { ChipTone } from './lineage-chip-tone';

/**
 * Title-cases an API code for display when no translation exists:
 * `PRE_ENRICH_SCORE` → `Pre enrich score`, `EXPERIMENT_GMC` → `Experiment gmc`.
 */
export function humanizeCode(code: string | null | undefined): string {
  if (!code) return '—';
  const words = code.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Vocabularies with a shared, human label map under `labels.<kind>` in the catalog-v2 namespace. */
export type CatalogLabelKind =
  | 'stage'
  | 'stageShort'
  | 'flow'
  | 'trigger'
  | 'ingestMode'
  | 'connector'
  | 'runStatus';

/**
 * The one way catalog-v2 screens turn an API code into user-facing text (never show raw
 * enums): looks up `labels.<kind>.<CODE>` in the `catalog-v2` namespace and falls back to a
 * humanised code, so a value the backend adds later still reads sensibly.
 * Pass the `t` from `useTranslation('catalog-v2')`.
 *
 * `destination` is the ONE hook every call site uses to make the DELIVERY stage read "Publish to
 * Google Merchant Center" instead of the plain "Publish": pass an already-resolved destination
 * name (e.g. from `catalogLabel(t, 'connector', connectorCode)`) when the caller has one in
 * context; omitted (or any other stage/kind), the label is unaffected.
 */
export function catalogLabel(
  t: TFunction<'catalog-v2'>,
  kind: CatalogLabelKind,
  code: string | null | undefined,
  opts?: { destination?: string | null },
): string {
  if (!code) return '—';
  // A hidden stage (Image check) reads as the stage it folds into — never its own name.
  const shown = kind === 'stage' || kind === 'stageShort' ? visibleStage(code) : code;
  if (kind === 'stage' && shown === 'DELIVERY' && opts?.destination) {
    return t('labels.stageDeliveryTo', { destination: opts.destination });
  }
  return t(`labels.${kind}.${shown}`, { defaultValue: humanizeCode(shown) });
}

/**
 * B1's stable `RunFailureCode` (15 values) — beside `failureReason` on `RunRead`, `RunProgress`,
 * `StageProgress`, `StageAttempt`, `FailurePoints` / `StageFailure`. Hand-typed: not yet in the
 * generated schema (`feat/attention-reasons-and-delivery-rejections`); verified against
 * catalog-service's `catalog_core/models/enums.py::RunFailureCode` — regenerate and drop this
 * once it lands. A client may see codes this list doesn't name yet (additive), so every reader
 * treats it as `string`, not this union, and falls back to a humanised code.
 */
export type RunFailureCode =
  | 'STAGE_STALLED'
  | 'GATE_EXPIRED'
  | 'PAUSE_EXPIRED'
  | 'OPERATOR_CANCELLED'
  | 'OPERATOR_REJECTED'
  | 'OPERATOR_RESET'
  | 'WORKFLOW_START_FAILED'
  | 'WORKFLOW_CANCELLED'
  | 'WORKER_ACTIVITY_FAILED'
  | 'FLOW_DEFINITION_MISSING'
  | 'STAGE_DISPATCH_REFUSED'
  | 'RECONCILIATION_FAILED'
  | 'EMPTY_ENRICHMENT'
  | 'CONTENT_STAGE_FAILED'
  | 'ARM_ASSIGNMENT_FAILED';

/** `failureCodeLabel`'s answer: a plain-words headline plus a one-line "what to do next". */
export interface FailureCodeLabel {
  code: string;
  /** Plain-words headline for the code — humanised when the locale has no entry for it yet. */
  title: string;
  /** One-line "what to do next"; empty when the locale has nothing to add. */
  next: string;
}

/**
 * The plain-words side of a `RunFailureCode`: a headline (`labels.failureCode.<CODE>.title`) and
 * a one-line next step (`labels.failureCode.<CODE>.next`), from the shared `catalog-v2` namespace
 * so every screen that shows a failure code (runs list, failure patterns, experiment header, the
 * run header) reads it the same way. `null` when there is no code — the caller falls back to
 * `failureReason` text alone, exactly as it did before `failureCode` existed.
 */
export function failureCodeLabel(
  t: TFunction<'catalog-v2'>,
  code: string | null | undefined,
): FailureCodeLabel | null {
  if (!code) return null;
  return {
    code,
    title: t(`labels.failureCode.${code}.title`, { defaultValue: humanizeCode(code) }),
    next: t(`labels.failureCode.${code}.next`, { defaultValue: '' }),
  };
}

/** Every spelling the APIs use for one connector → its wire code (`GMC`, `META`, `SHOPIFY`). */
const WIRE_CONNECTOR: Record<string, string> = {
  GMC: 'GMC',
  GOOGLE_MERCHANT: 'GMC',
  META: 'META',
  META_COMMERCE: 'META',
  SHOPIFY: 'SHOPIFY',
};

/**
 * Normalises a connector code to its wire form. The catalog APIs mix the wire code
 * (`GMC`, from labels/trigger) with the storage code (`GOOGLE_MERCHANT`, from curation
 * reads) and lower-case feed sources; compare connectors only after this.
 */
export function toWireConnector(code: string | null | undefined): string | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  return WIRE_CONNECTOR[upper] ?? upper;
}

/**
 * Badge tone of a connector on the Batches list (#175): Google Merchant Center blue, Meta Commerce
 * indigo (`primary`), anything else neutral — so a column of batches scans by destination.
 */
export function connectorTone(code: string | null | undefined): ChipTone {
  switch (toWireConnector(code)) {
    case 'GMC':
      return 'info';
    case 'META':
      return 'primary';
    default:
      return 'neutral';
  }
}

/**
 * Badge tone of how a batch started (#175): Experiment violet, Scheduled `primary` (the theme has
 * no teal/cyan token), Manual / Retry / a trigger added later neutral.
 */
export function runTypeTone(trigger: string | null | undefined): ChipTone {
  switch (trigger) {
    case 'EXPERIMENT':
      return 'violet';
    case 'SCHEDULE':
      return 'primary';
    default:
      return 'neutral';
  }
}
